// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { createBrowserRouter } from 'react-router-dom'
import { AppShell }       from '@/layouts/AppShell'
import { HomeLayout }     from '@/layouts/HomeLayout'
import { AuthLayout }     from '@/layouts/AuthLayout'
import { NextOfKinShell } from '@/layouts/NextOfKinShell'
import { ProtectedRoute, NextOfKinRoute, PublicRoute } from './ProtectedRoute'
import { ROUTES, WIZARD_ROUTE, DOSSIER_NOTARIS, DOSSIER_NABESTAANDEN, DOSSIER_OVERVIEW, DOSSIER_UITVAART, HOME_ROUTE } from '@/lib/constants/routes'

// Auth
import { LoginPage }        from '@/pages/auth/LoginPage'
import { OnboardingPage }   from '@/pages/auth/OnboardingPage'
import { DemoPreviewPage }  from '@/pages/demo-preview/DemoPreviewPage'
// Wizard
import { GuidedWizardPage } from '@/pages/wizard/GuidedWizardPage'

// App pages
import { DashboardPage }        from '@/pages/dashboard/DashboardPage'
import { IntakePage }           from '@/pages/intake/IntakePage'
import { PartnerPage }          from '@/pages/partner/PartnerPage'
import { FinancialPage }        from '@/pages/financial/FinancialPage'
import { WishesPage }           from '@/pages/wishes/WishesPage'
import { MedicalWishesPage }    from '@/pages/wishes/MedicalWishesPage'
import { FuneralWishesPage }    from '@/pages/wishes/FuneralWishesPage'
import { FuneralCostsPage }     from '@/pages/wishes/FuneralCostsPage'
import { PersonalFarewellPage } from '@/pages/wishes/PersonalFarewellPage'
import { TrustedPersonPage }    from '@/pages/trusted-person/TrustedPersonPage'
import { MessagesPage }         from '@/pages/messages/MessagesPage'
import { DocumentIndexPage }    from '@/pages/document-index/DocumentIndexPage'
import { MunicipalityPage }     from '@/pages/municipality/MunicipalityPage'
import { AIGuidePage }          from '@/pages/ai-guide/AIGuidePage'
import { ControlCenterPage }    from '@/pages/control-center/ControlCenterPage'
import { NotarialPage }           from '@/pages/notarial/NotarialPage'
import { LevenslijnPage }          from '@/pages/levenslijn/LevenslijnPage'
import { EuthanasieInfoPage }      from '@/pages/levenslijn/EuthanasieInfoPage'
import { VehiclesPage }           from '@/pages/vehicles/VehiclesPage'
import { BusinessPage }           from '@/pages/business/BusinessPage'
import { HomePage }             from '@/pages/home/HomePage'
import { NotarisDossierPage }   from '@/pages/dossier/NotarisDossierPage'
import { UitvaartDossierPage }  from '@/pages/dossier/UitvaartDossierPage'
import { DossiersPage }            from '@/pages/dossier/DossiersPage'
import { NabestaandenDossierPage } from '@/pages/dossier/NabestaandenDossierPage'
import { SettingsPage }         from '@/pages/settings/SettingsPage'

// Next of kin
import { NextOfKinEntryPage } from '@/pages/next-of-kin/NextOfKinEntryPage'
import { NextOfKinModePage }  from '@/pages/next-of-kin/NextOfKinModePage'

// Error
import { NotFoundPage } from '@/pages/error/NotFoundPage'
import { PrivacyPage }  from '@/pages/legal/PrivacyPage'
import { TermsPage }    from '@/pages/legal/TermsPage'

export const router = createBrowserRouter([
  // ── Public ────────────────────────────────────────────────────────────────
  {
    element: <PublicRoute />,
    children: [{
      element: <AuthLayout />,
      children: [
        { path: ROUTES.LOGIN,      element: <LoginPage /> },
        { path: ROUTES.ONBOARDING, element: <OnboardingPage /> },
      ],
    }],
  },

  // ── Demo preview (volledig publiek - geen login, geen AuthLayout) ─────────
  { path: ROUTES.DEMO_PREVIEW, element: <DemoPreviewPage /> },

  // ── Next-of-kin entry ─────────────────────────────────────────────────────
  {
    element: <AuthLayout />,
    children: [
      { path: ROUTES.NEXT_OF_KIN_ENTRY, element: <NextOfKinEntryPage /> },
    ],
  },

  // ── Protected: Wizard (fullscreen - outside AppShell) ─────────────────────
  {
    element: <ProtectedRoute />,
    children: [
      { path: WIZARD_ROUTE, element: <GuidedWizardPage /> },
    ],
  },

  // ── Protected: Home (welkomstpagina - geen sidebar) ─────────────────────────
  {
    element: <ProtectedRoute />,
    children: [{
      element: <HomeLayout />,
      children: [
        { path: HOME_ROUTE, element: <HomePage /> },
      ],
    }],
  },

  // ── Protected: App shell ──────────────────────────────────────────────────
  {
    element: <ProtectedRoute />,
    children: [{
      element: <AppShell />,
      children: [
        { path: ROUTES.DASHBOARD,       element: <DashboardPage /> },
        { path: ROUTES.INTAKE,          element: <IntakePage /> },
        { path: ROUTES.PARTNER,         element: <PartnerPage /> },
        { path: ROUTES.FINANCIAL,       element: <FinancialPage /> },
        { path: ROUTES.WISHES,          element: <WishesPage /> },
        { path: ROUTES.WISHES_MEDICAL,  element: <MedicalWishesPage /> },
        { path: ROUTES.WISHES_FUNERAL,        element: <FuneralWishesPage /> },
        { path: ROUTES.WISHES_FUNERAL_COSTS,  element: <FuneralCostsPage /> },
        { path: ROUTES.WISHES_FAREWELL, element: <PersonalFarewellPage /> },
        { path: ROUTES.TRUSTED_PERSON,  element: <TrustedPersonPage /> },
        { path: ROUTES.MESSAGES,        element: <MessagesPage /> },
        { path: ROUTES.DOCUMENTS,       element: <DocumentIndexPage /> },
        { path: ROUTES.MUNICIPALITY,    element: <MunicipalityPage /> },
        { path: ROUTES.AI_GUIDE,        element: <AIGuidePage /> },
        { path: ROUTES.CONTROL_CENTER,  element: <ControlCenterPage /> },
        { path: ROUTES.NOTARIAL,        element: <NotarialPage /> },
        { path: ROUTES.LEVENSLIJN,      element: <LevenslijnPage /> },
        { path: ROUTES.EUTHANASIE_INFO, element: <EuthanasieInfoPage /> },
        { path: ROUTES.VEHICLES,        element: <VehiclesPage /> },
        { path: ROUTES.BUSINESS,        element: <BusinessPage /> },
        { path: ROUTES.SETTINGS,          element: <SettingsPage /> },
        { path: DOSSIER_NOTARIS,          element: <NotarisDossierPage /> },
        { path: DOSSIER_UITVAART,         element: <UitvaartDossierPage /> },
        { path: DOSSIER_NABESTAANDEN,     element: <NabestaandenDossierPage /> },
        { path: DOSSIER_OVERVIEW,         element: <DossiersPage /> },
      ],
    }],
  },

  // ── Next-of-kin mode ──────────────────────────────────────────────────────
  {
    element: <NextOfKinRoute />,
    children: [{
      element: <NextOfKinShell />,
      children: [
        { path: ROUTES.NEXT_OF_KIN,        element: <NextOfKinModePage /> },
        { path: `${ROUTES.NEXT_OF_KIN}/*`, element: <NextOfKinModePage /> },
      ],
    }],
  },

  // ── 404 ───────────────────────────────────────────────────────────────────
  { path: '/privacy',       element: <PrivacyPage /> },
  { path: '/voorwaarden',   element: <TermsPage /> },
  { path: '*', element: <NotFoundPage /> },
], {
  basename: '/demo',
  future: {
    v7_relativeSplatPath:           true,
    v7_fetcherPersist:              true,
    v7_normalizeFormMethod:         true,
    v7_partialHydration:            true,
    v7_skipActionErrorRevalidation: true,
  },
})
